<script setup lang="ts">
import type { LibraryFiltersModel } from '@/js/account/entities/document/model/types';
import { computed, ref } from 'vue';
import BaseBottomSheet from '@/js/account/components/base/BaseBottomSheet.vue';
import BaseButton from '@/js/account/components/base/BaseButton.vue';
import BaseIcon from '@/js/account/components/base/BaseIcon.vue';
import BaseTitle from '@/js/account/components/base/BaseTitle.vue';
import LibraryFiltersPanel from '@/js/account/components/library/LibraryFiltersPanel.vue';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    modelValue?: LibraryFiltersModel
    loading?: boolean
  }>(),
  {
    modelValue: () => ({
      categoryIds: [],
      tagIds: [],
    }),
    loading: false,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: LibraryFiltersModel]
  'apply': [value: LibraryFiltersModel]
  'clean': []
}>();

const cn = classNames('library-filters');
const mobilePanelRef = ref<InstanceType<typeof LibraryFiltersPanel> | null>(null);
const desktopPanelRef = ref<InstanceType<typeof LibraryFiltersPanel> | null>(null);
const isSheetOpen = ref(false);

const activeCount = computed(
  () => props.modelValue.categoryIds.length + props.modelValue.tagIds.length,
);

function activePanel(
  fromSheet: boolean,
): InstanceType<typeof LibraryFiltersPanel> | null {
  return fromSheet ? mobilePanelRef.value : desktopPanelRef.value;
}

function apply(fromSheet = false): void {
  const value = activePanel(fromSheet)?.commitDraft();

  if (value) {
    emit('apply', value);

    if (fromSheet) {
      isSheetOpen.value = false;
    }
  }
}

function clean(fromSheet = false): void {
  activePanel(fromSheet)?.resetDraft();
  emit('clean');

  if (fromSheet) {
    isSheetOpen.value = false;
  }
}
</script>

<template>
  <div :class="cn()">
    <button
      type="button"
      :class="cn('mobile-trigger', { open: isSheetOpen })"
      :aria-expanded="isSheetOpen"
      aria-haspopup="dialog"
      aria-label="Filters"
      @click="isSheetOpen = true"
    >
      <BaseIcon name="sliders" :width="16" :height="16" :class="cn('icon')" />
      <span :class="cn('label')">Filters</span>
      <span v-if="activeCount > 0" :class="cn('badge')">
        {{ activeCount }}
      </span>
    </button>

    <BaseBottomSheet v-model:open="isSheetOpen" title="Filters">
      <LibraryFiltersPanel
        ref="mobilePanelRef"
        :model-value="modelValue"
        @update:model-value="emit('update:modelValue', $event)"
      />

      <div :class="cn('actions')">
        <BaseButton
          theme="primary"
          full-width
          :loading="loading"
          size="sm"
          @click="apply(true)"
        >
          Apply
        </BaseButton>
        <BaseButton
          theme="tertiary"
          full-width
          :disabled="loading"
          size="sm"
          @click="clean(true)"
        >
          Clean
        </BaseButton>
      </div>
    </BaseBottomSheet>

    <section :class="cn('desktop')">
      <BaseTitle size="sm" :class="cn('title')">
        Filters
      </BaseTitle>
      <hr :class="cn('divider')">

      <LibraryFiltersPanel
        ref="desktopPanelRef"
        :model-value="modelValue"
        @update:model-value="emit('update:modelValue', $event)"
      />

      <div :class="cn('actions')">
        <BaseButton
          theme="primary"
          full-width
          :loading="loading"
          size="sm"
          @click="apply()"
        >
          Apply
        </BaseButton>
        <BaseButton
          theme="tertiary"
          full-width
          :disabled="loading"
          size="sm"
          @click="clean()"
        >
          Clean
        </BaseButton>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.library-filters {
  &__mobile-trigger {
    box-sizing: border-box;
    display: none;
    flex: 1;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;
    min-width: 0;
    padding: 10px 16px;
    color: var(--color-primary);
    font-weight: 400;
    font-size: 14px;
    font-family: var(--font-family-sans);
    line-height: 1.2;
    text-align: left;
    background-color: var(--color-white);
    border: none;
    border-radius: 4px;
    box-shadow: var(--shadow);
    cursor: pointer;
    transition: box-shadow 0.2s;
    max-width: max-content;

    &:hover,
    &_open {
      box-shadow: 0 0 12px 2px rgba(214, 214, 214, 0.55);
    }
  }

  &__icon {
    flex-shrink: 0;
    color: var(--color-primary);
  }

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__badge {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    color: var(--color-white);
    font-size: 12px;
    font-weight: 500;
    line-height: 1;
    background-color: var(--color-primary);
    border-radius: 999px;
  }

  &__desktop {
    box-sizing: border-box;
    padding: 16px;
    background-color: #fff;
    border: 1px solid var(--base-field-border-color, #e0e0e0);
    border-radius: 4px;
  }

  &__divider {
    margin: 16px -16px;
    border: none;
    width: calc(100% + 32px);
    border-top: 1px solid var(--base-field-border-color, #e0e0e0);
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 40px;
  }

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;

    &__mobile-trigger {
      display: inline-flex;
      width: 100%;
      max-width: none;
    }

    &__desktop {
      display: none;
    }
  }
}
</style>
